import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import {
  TrendingUp, TrendingDown, Activity, Plane, BarChart3, CheckCircle,
  Star, ChevronRight,
} from 'lucide-react';
import HourlyPatternChart from '@/components/HourlyPatternChart';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const LEVEL_PILL = {
  low: 'bg-blue-50 text-blue-700',
  medium: 'bg-indigo-50 text-indigo-700',
  high: 'bg-amber-50 text-amber-700',
  extreme: 'bg-rose-50 text-rose-700',
};

const DOW_FULL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Unique data-driven content block for an airport guide page. Pulls from
 * /api/airport/insights/{code} which is deterministic so the page renders
 * the same numbers on every load (great for SEO).
 */
export default function AirportInsightsSection({ airportCode }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!airportCode) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/airport/insights/${airportCode}`);
        if (r.ok && !cancel) setInsights(await r.json());
      } catch {
        /* silent */
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [airportCode]);

  if (loading) {
    return (
      <Card className="border-slate-200 bg-white">
        <CardContent className="p-6">
          <div className="h-40 bg-slate-100 rounded animate-pulse" />
        </CardContent>
      </Card>
    );
  }
  if (!insights) return null;

  const code = insights.airport_code;
  const maxDow = Math.max(...insights.dow_pattern.map((d) => d.crowd_score));

  return (
    <div className="space-y-6" data-testid={`insights-section-${code}`}>
      {/* Headline data summary */}
      <Card className="border-slate-200 bg-white">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900 heading flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-500" />
              {code} crowd patterns — by the numbers
            </h2>
            <span className="text-[11px] text-slate-400 inline-flex items-center gap-1">
              <Activity className="w-3 h-3" />
              From our model
            </span>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed mb-5">
            We modeled a full typical week at {code} — every hour, every day, every aircraft size — to surface patterns
            that show up week-over-week. Here's what {insights.airport_name.replace(/^Hartsfield-Jackson |^John F\. Kennedy |^Los Angeles |^O'Hare /, '')} actually looks like.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KeyStat
              icon={<TrendingUp className="w-3.5 h-3.5 text-rose-500" />}
              label="Peak hour"
              value={insights.peak_hour?.label}
              sub="busiest typical time"
            />
            <KeyStat
              icon={<TrendingDown className="w-3.5 h-3.5 text-emerald-500" />}
              label="Quietest hour"
              value={insights.quietest_hour?.label}
              sub="easiest TSA"
            />
            <KeyStat
              icon={<Plane className="w-3.5 h-3.5 text-blue-500" />}
              label="Daily flights"
              value={Math.round(insights.daily_average_flights)}
              sub="dep + arr typical"
            />
            <KeyStat
              icon={<Activity className="w-3.5 h-3.5 text-indigo-500" />}
              label="Weekday vs weekend"
              value={`${insights.weekday_score.toFixed(1)} / ${insights.weekend_score.toFixed(1)}`}
              sub={insights.weekday_score > insights.weekend_score ? 'weekdays busier' : 'weekends busier'}
            />
          </div>
        </CardContent>
      </Card>

      {/* Hour-by-hour chart */}
      <HourlyPatternChart
        data={insights.hourly_pattern}
        title={`When is ${code} actually busy?`}
        subtitle="Crowd score by local hour, averaged across the typical week. Hover any bar for the exact value."
        dataTestId={`hourly-chart-${code}`}
      />

      {/* Best & worst times */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-emerald-100 bg-emerald-50/30">
          <CardContent className="p-5">
            <h3 className="text-base font-semibold text-slate-900 heading mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              Best times to fly out of {code}
            </h3>
            <ul className="space-y-3">
              {insights.best_times.map((t, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`best-time-${i}`}>
                  <span className="text-2xl font-bold text-emerald-600 leading-none w-12">{t.label}</span>
                  <div className="flex-1">
                    <p className="text-sm text-slate-700">{t.reason}</p>
                    <span className={`inline-block text-[10px] uppercase tracking-wide mt-1 px-1.5 py-0.5 rounded ${LEVEL_PILL[t.crowd_level]}`}>
                      {t.crowd_level}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="border-rose-100 bg-rose-50/30">
          <CardContent className="p-5">
            <h3 className="text-base font-semibold text-slate-900 heading mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-rose-500" />
              Worst times to fly out of {code}
            </h3>
            <ul className="space-y-3">
              {insights.worst_times.map((t, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`worst-time-${i}`}>
                  <span className="text-2xl font-bold text-rose-600 leading-none w-12">{t.label}</span>
                  <div className="flex-1">
                    <p className="text-sm text-slate-700">{t.reason}</p>
                    <span className={`inline-block text-[10px] uppercase tracking-wide mt-1 px-1.5 py-0.5 rounded ${LEVEL_PILL[t.crowd_level]}`}>
                      {t.crowd_level}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Day-of-week pattern */}
      <Card className="border-slate-200 bg-white">
        <CardContent className="p-5 sm:p-6">
          <h3 className="text-base font-semibold text-slate-900 heading mb-1">
            Day of the week: when is {code} most painful?
          </h3>
          <p className="text-xs text-slate-500 mb-4">
            Avg crowd score across daytime hours (6 AM – 10 PM local).
          </p>
          <div className="grid grid-cols-7 gap-1 sm:gap-2 items-end h-32">
            {insights.dow_pattern.map((d, idx) => {
              const h = Math.max(12, (d.crowd_score / maxDow) * 100);
              const meta = LEVEL_PILL[d.crowd_level] || LEVEL_PILL.medium;
              return (
                <motion.div
                  key={idx}
                  initial={{ height: 0 }}
                  animate={{ height: `${h}%` }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                  className={`rounded-t flex items-end justify-center ${
                    d.crowd_level === 'low' ? 'bg-blue-400' :
                    d.crowd_level === 'medium' ? 'bg-indigo-400' :
                    d.crowd_level === 'high' ? 'bg-amber-400' : 'bg-rose-500'
                  }`}
                  title={`${d.full_label}: ${d.crowd_score}`}
                >
                  <span className="text-[10px] text-white font-bold pb-1 hidden sm:inline">{d.crowd_score}</span>
                </motion.div>
              );
            })}
          </div>
          <div className="grid grid-cols-7 gap-1 sm:gap-2 mt-2 text-center">
            {DOW_FULL.map((label) => (
              <div key={label} className="text-[10px] sm:text-xs text-slate-500">{label}</div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Aircraft mix */}
      <Card className="border-slate-200 bg-white">
        <CardContent className="p-5 sm:p-6">
          <h3 className="text-base font-semibold text-slate-900 heading mb-1">
            Aircraft mix at peak hour
          </h3>
          <p className="text-xs text-slate-500 mb-4">
            Wide-body and regional jets disproportionately affect crowd levels — they unload at very different rates.
          </p>
          <div className="space-y-3">
            {insights.aircraft_mix.map((m) => (
              <div key={m.type}>
                <div className="flex items-center justify-between mb-1 text-xs">
                  <span className="text-slate-700 font-medium">{m.type}</span>
                  <span className="text-slate-500">{m.count} aircraft · {m.percent}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${m.percent}%` }}
                    transition={{ duration: 0.6 }}
                    className={
                      m.type.includes('Wide') ? 'h-full bg-rose-400' :
                      m.type.includes('Regional') ? 'h-full bg-amber-400' :
                      'h-full bg-blue-400'
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Comparison with other hubs */}
      {insights.comparison?.length > 0 && (
        <Card className="border-slate-200 bg-white">
          <CardContent className="p-5 sm:p-6">
            <h3 className="text-base font-semibold text-slate-900 heading mb-1">
              How {code} compares to other major hubs
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Avg crowd score across daytime hours. Lower = easier travel day.
            </p>
            <ul className="space-y-2">
              {insights.comparison.map((c) => (
                <li
                  key={c.code}
                  className={`flex items-center justify-between gap-3 p-2 rounded ${c.is_focus ? 'bg-blue-50' : ''}`}
                >
                  <Link
                    to={`/airports/${c.code.toLowerCase()}`}
                    className="flex items-center gap-2 flex-1 min-w-0 group"
                  >
                    {c.is_focus && <Star className="w-3 h-3 text-amber-400 fill-amber-400" />}
                    <span className="font-mono font-semibold text-slate-900 w-12">{c.code}</span>
                    <span className="text-sm text-slate-500 truncate group-hover:text-slate-800">{c.city}</span>
                  </Link>
                  <div className="flex items-center gap-3">
                    <div className="hidden sm:block w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-400"
                        style={{ width: `${Math.min(100, (c.avg_crowd_score / 15) * 100)}%` }}
                      />
                    </div>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${LEVEL_PILL[c.crowd_level]}`}>
                      {c.avg_crowd_score.toFixed(1)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Feedback summary if any */}
      {insights.feedback_summary?.total_reports > 0 && (
        <Card className="border-slate-200 bg-white">
          <CardContent className="p-5">
            <h3 className="text-base font-semibold text-slate-900 heading mb-2">
              What real travelers say
            </h3>
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{insights.feedback_summary.total_reports}</span> traveler reports have calibrated our {code} predictions
              {insights.feedback_summary.accuracy_pct !== null && (
                <> · we're matching reality <span className="font-semibold">{insights.feedback_summary.accuracy_pct}%</span> of the time</>
              )}
              {insights.feedback_summary.recent_reports > 0 && (
                <> · <span className="font-semibold">{insights.feedback_summary.recent_reports}</span> reports in the last 14 days</>
              )}
              .
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KeyStat({ icon, label, value, sub }) {
  return (
    <div className="p-3 bg-slate-50 rounded-lg">
      <div className="flex items-center gap-1 text-[10px] sm:text-xs uppercase tracking-wide text-slate-500 font-medium">
        {icon}
        {label}
      </div>
      <div className="text-base sm:text-lg font-semibold text-slate-900 mt-1 heading">{value ?? '—'}</div>
      <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>
    </div>
  );
}
